import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import {
  selectGotNotificationsList,
  selectHotelList,
  selectNotificationPreferences,
  selectNotifications
} from '../../accessors/index'
import { getNotificationPreferences, getNotifications } from '../../actions/notifications'
import CloseIcon from '../../components/ReusableComponents/CloseIcon/index'
import NotificationCard from '../../components/ReusableComponents/NotificationCard/index'
import RefreshIcon from '../../components/ReusableComponents/RefreshIcon/index'
import useClickOut from '../../hooks/useClickOut'
import HotelGroup from './HotelGroup/index'
import styles from './NotificationCenter.module.scss'
import { ReactComponent as SettingsIcon } from './settings.svg'
import WelcomeCard from './WelcomeCard/index'

const NotificationCenter = ({ handleHide, visible }) => {
  const dispatch = useDispatch()

  // All useSelector hooks must be called at the top level
  const notificationPreferences = useSelector(selectNotificationPreferences)
  const notificationsData = useSelector(selectNotifications)
  const gotNotificationsList = useSelector(selectGotNotificationsList)
  const hotels = useSelector(selectHotelList)
  const isGettingNotifications = useSelector(s => s.notifications.isGettingNotifications)

  // All useState hooks
  const [hasEnabledNotifications, setHasEnabledNotifications] = useState(false)
  const [onPreClose, setOnPreClose] = useState(false)

  // All useRef hooks
  const wrapperRef = useRef()

  // Custom hooks
  useClickOut(wrapperRef, handleClickOut)

  // All useEffect hooks
  useEffect(() => {
    if (notificationPreferences) {
      setHasEnabledNotifications(
        notificationPreferences.some(n => n.subsidiaries.length > 0)
      )
    }
  }, [notificationPreferences])

  useEffect(() => {
    if (!notificationPreferences) {
      dispatch(getNotificationPreferences())
    }
  }, [dispatch, notificationPreferences])

  useEffect(() => {
    if (hasEnabledNotifications && !gotNotificationsList) {
      dispatch(getNotifications())
    }
  }, [dispatch, gotNotificationsList, hasEnabledNotifications])

  // Functions and conditional logic after all hooks
  function handleClose() {
    setOnPreClose(true)
    setTimeout(() => {
      handleHide()
      setOnPreClose(false)
    }, 300)
  }

  function handleClickOut() {
    if (visible) {
      handleClose()
    }
  }

  if (!visible) {
    return null
  }

  return (
    <div
      className={onPreClose ? styles.wrapperPreClose : styles.wrapper}
      ref={wrapperRef}
    >
      <div className={styles.title}>
        Notifications
        <button
          className={styles.refreshButton}
          onClick={() => dispatch(getNotifications())}
        >
          <RefreshIcon className={isGettingNotifications ? styles.spin : ''} />
        </button>
        <button onClick={handleClose} className={styles.closeButton}>
          <CloseIcon />
        </button>
      </div>
      <div className={styles.hotelGroups}>
        {!hasEnabledNotifications && <WelcomeCard handleClick={handleClose} />}
        {hasEnabledNotifications &&
          Object.keys(notificationsData).map(hotelId => (
            <HotelGroup
              key={hotelId}
              hotelName={
                hotels.find(h => h.subsidiaryId === hotelId)
                  ? hotels.find(h => h.subsidiaryId === hotelId).subsidiaryName
                  : 'Unknown hotel'
              }
            >
              {Object.keys(notificationsData[hotelId]).map(n => (
                <NotificationCard
                  key={n}
                  count={notificationsData[hotelId][n].count}
                  hotelId={hotelId}
                  kind={n}
                  handleClick={handleClose}
                />
              ))}
            </HotelGroup>
          ))}
      </div>
      <Link to="/notificationPreferences" className={styles.preferencesLink}>
        <button className={styles.preferencesBox} onClick={handleClose}>
          <SettingsIcon />
          Notification preferences
        </button>
      </Link>
    </div>
  )
}

export default NotificationCenter
